/**
 * Payment Method Controller
 *
 * A custom React hook that manages the Stripe payment flow for membership subscriptions.
 * This controller handles multiple states of the payment process:
 *
 * 1. Price calculation and display - Manages subtotal, discounts, taxes, and final amount
 * 2. Coupon validation and application - Validates coupon codes and applies discounts
 * 3. Waitlist/Invite flow - Handles users with invite codes or joining waitlist
 * 4. Payment submission - Processes payment through Stripe and handles responses
 * 5. Error handling - Provides user feedback for various error conditions
 *
 * The component integrates with Stripe Elements for secure payment collection
 * and calls membership APIs to validate pricing and create subscriptions.
 */

import { useMeControllerGetUserInfo } from '@/api/generated/default/default';
import {
  useMembershipControllerConfirmSubscriptionV1,
  useMembershipControllerConfirmTrialVerification,
  useMembershipControllerCreateSubscriptionV1,
  useMembershipControllerCreateTrialVerification,
  useMembershipControllerGetUserPrice,
} from '@/api/generated/membership/membership';
import { PaymentError } from '@/app/errors/payment-error';
import { PaymentFormState } from '@/app/components/ppra/components/ppra-payment-page/PPRAPaymentPage';
import { ROUTE_DASHBOARD, ROUTE_PAYMENT_VERIFY_PAYMENT_V2, ROUTE_QUESTIONNAIRE_V2, ROUTE_SIGN_IN_ENTER_EMAIL } from '@/constants/routes';
import { TOAST_DEFAULT_OPTIONS } from '@/constants/toast';
import useAnalyticsCalls from '@/hooks/use-analytics';
import usePureState from '@/hooks/use-pure-state';
import useReceiveInviteCode from '@/hooks/use-receive-invite-code';
import useOnMount from '@/hooks/useOnMount';
import useVisitorSession from '@/hooks/useVisitorSession';
import authService from '@/services/auth.service';
import envService from '@/services/env.service';
import { FeatureFlags, useFeatureFlags } from '@/services/feature-flag-provider';
import { useToast } from '@cmpsr/components';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeExpressCheckoutElementConfirmEvent } from '@stripe/stripe-js';
import { AxiosError } from 'axios';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import getCACToastRender from './components/get-cac-toast-render';

// Types for price data structure used throughout the component
export type PriceItem = {
  description: string;
  amount: number;
};

export type PriceDataType = {
  lines: Array<PriceItem>;
  summary: PriceItem;
};

// Helper function to build price data from individual components
export const buildPriceData = (
  subtotal: number | undefined,
  discount: number | undefined,
  tax: number | undefined,
  total: number | undefined
): PriceDataType => {
  const lines = [{ description: 'SUBTOTAL', amount: subtotal ?? 0 }];

  if (discount && discount > 0) lines.push({ description: 'DISCOUNT', amount: discount });
  if (tax && tax > 0) lines.push({ description: 'TAX', amount: tax });

  return { lines, summary: { description: 'TOTAL', amount: total ?? 0 } };
};

// State type for the payment method hook
type useCollectPaymentMethodState = {
  priceData: PriceDataType;
  validationError: string | false | null;
  analyticsDataSent: boolean;
  isCouponVisible: boolean;
  zip: string | null;
  coupon: string;
  tempCoupon: string;
  confirmedAccount: boolean;
  clearedWaitList: boolean;
  isEmailEditable: boolean;
  isSubmitting: boolean;
  paymentScreenAnalyticsSent: boolean;
  inviteCode: string | null;
};

export default function useCollectPaymentMethodV2() {
  // Core API mutations and hooks for payment processing
  const membershipMutation = useMembershipControllerCreateSubscriptionV1();
  const trialVerificationMutation = useMembershipControllerCreateTrialVerification();
  const confirmTrialVerificationMutation = useMembershipControllerConfirmTrialVerification();
  const confirmSubscriptionMutation = useMembershipControllerConfirmSubscriptionV1();
  const analytics = useAnalyticsCalls();
  const elements = useElements();
  const navigate = useNavigate();
  const { isEnabled } = useFeatureFlags();
  const isSinglePaymentSwitch = envService.getEnv().VITE_SWITCH_SINGLE_PAYMENT_ENABLED === 'true';
  const isSinglePaymentFlag = isEnabled(FeatureFlags.IsSinglePaymentEnabled);
  const isSinglePaymentEnabled = isSinglePaymentSwitch || isSinglePaymentFlag;

  const stripe = useStripe();
  const toast = useToast();
  const [params] = useSearchParams();
  const errorToShow = params.get('error');
  const isTrial = params.get('trial') === 'true';
  const { initializeSession, email, captureLead } = useVisitorSession();
  const isPpraInviteRequired = envService.getEnv().VITE_SWITCH_PPRA_INVITE_CODE_REQUIRED === 'true';
  const { inviteCode: inviteCodeFromReceiveInviteCode } = useReceiveInviteCode();
  const { data: memberData } = useMeControllerGetUserInfo();

  useEffect(() => {
    if (memberData?.userLevel === 'member') {
      navigate(ROUTE_DASHBOARD);
    }
  }, [memberData]);

  // Initialize component state with default values
  const { state, setPartialState } = usePureState<useCollectPaymentMethodState>({
    zip: '',
    coupon: '',
    tempCoupon: '',
    priceData: buildPriceData(0, 0, 0, 0),
    validationError: null,
    analyticsDataSent: false,
    isCouponVisible: false,
    confirmedAccount: false,
    // Determines if waitlist message is shown. If invite code is provided, we assume the user has been cleared from waitlist.
    clearedWaitList: inviteCodeFromReceiveInviteCode ? true : false,
    isEmailEditable: true,
    isSubmitting: false,
    paymentScreenAnalyticsSent: false,
    inviteCode: inviteCodeFromReceiveInviteCode,
  });

  const {
    zip,
    coupon,
    tempCoupon,
    priceData,
    validationError,
    analyticsDataSent,
    isCouponVisible,
    clearedWaitList,
    isEmailEditable,
    isSubmitting,
    paymentScreenAnalyticsSent,
    inviteCode,
  } = state;

  // Fetch pricing data based on current coupon and zip
  const { data: getPriceData, mutateAsync: refetch, isPending } = useMembershipControllerGetUserPrice();

  // Helper functions for coupon handling
  const setCoupon = (tempCoupon: string) => setPartialState({ tempCoupon, validationError: false });
  const showCoupon = () => setPartialState({ isCouponVisible: true, validationError: false });
  const setIsEmailEditable = (isEmailEditable: boolean) => setPartialState({ isEmailEditable });

  // Reset validation error when coupon changes
  useEffect(() => setPartialState({ validationError: null }), [coupon, setPartialState]);

  // Track analytics revenue when price data is available
  useEffect(() => {
    if (!priceData || analyticsDataSent || priceData.summary.amount <= 0) return;
    setPartialState({ analyticsDataSent: true });
    analytics.callRevenue(priceData.summary.amount / 100, 'draft');
  }, [priceData?.summary.amount]);

  // Initialize form with default values
  const defaultValues = {
    email: email ?? '',
    legalAge: false,
    autoRenewal: true,
    coupon: getPriceData?.defaultCoupon ?? '',
    zip: '',
    inviteCode: inviteCode ?? undefined,
  };
  const form = useForm<PaymentFormState>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues,
  });

  // Updates the coupon value in the form
  const updateCouponFormField = (coupon: string) => {
    if (!form.getValues('coupon')) form.register('coupon');
    form.setValue('coupon', coupon ?? '');
  };

  /**
   * Fetches updated pricing information based on the current coupon and postal code values.
   * Updates the price data state, validates the coupon, and updates the Stripe elements
   * with the new total amount if elements are available.
   */
  const getPrice = async () => {
    await setPartialState({ zip: form.getValues('zip'), coupon: tempCoupon });
    const response = await refetch({
      data: {
        inviteCode: inviteCode ?? undefined,
        coupon: tempCoupon,
        zipCode: form.getValues('zip') || undefined,
        isTrial,
      },
    });
    if (!response) {
      console.error('Failed to fetch price data');
      return;
    }
    updateCouponFormField(tempCoupon);

    setPartialState({
      priceData: buildPriceData(response.subtotal, response.discount, response.tax, response.total),
      validationError: tempCoupon && (!response.discount || response.discount === 0) ? 'Invalid coupon' : false,
    });

    // Dispatch event to update parent Elements container
    if (response.total > 0) {
      window.dispatchEvent(new CustomEvent('stripe-amount-update', {
        detail: { amount: response.total }
      }));
    }

    if (elements)
      elements.update({
        amount: response.total,
        currency: 'usd',
      });
  };

  // Initial price data setup when data is first received
  useEffect(() => {
    if (getPriceData?.subtotal && getPriceData?.total && (!priceData || priceData.summary.amount === 0)) {
      const newCoupon = getPriceData?.defaultCoupon ?? tempCoupon;
      setPartialState({
        priceData: buildPriceData(getPriceData?.subtotal, getPriceData?.discount, getPriceData?.tax, getPriceData?.total),
        tempCoupon: newCoupon,
        isCouponVisible: !!newCoupon,
      });

      // Dispatch event to update parent Elements container
      if (getPriceData.total > 0) {
        window.dispatchEvent(new CustomEvent('stripe-amount-update', {
          detail: { amount: getPriceData.total }
        }));
      }

      if (!paymentScreenAnalyticsSent) {
        analytics.callPaymentScreenViewed(
          (getPriceData?.subtotal ?? 0) / 100,
          (getPriceData?.discount ?? 0) / 100,
          (getPriceData?.total ?? 0) / 100
        );
        setPartialState({ paymentScreenAnalyticsSent: true });
      }
    }
  }, [getPriceData?.subtotal, getPriceData?.discount, getPriceData?.tax, getPriceData?.total, getPriceData?.defaultCoupon, priceData, tempCoupon, paymentScreenAnalyticsSent, analytics, setPartialState]);

  // Function to join waitlist with email and invite code
  const joinWaitlist = async () => {
    try {
      const email = form.getValues('email');
      const inviteCode = form.getValues('inviteCode');
      if (!email) {
        return;
      }
      const leadResponse = await captureLead(email, inviteCode);
      setPartialState({ confirmedAccount: true });
      setPartialState({ inviteCode: leadResponse.inviteCode });
      if (leadResponse.inviteCode) {
        form.clearErrors('inviteCode');
        setPartialState({ clearedWaitList: true });
      } else {
        if (isPpraInviteRequired) {
          form.setError('inviteCode', { type: 'validate', message: 'Invalid invite code' });
        }
      }
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 404) {
        toast({
          title: 'Error',
          description: 'Invalid invite code',
          status: 'error',
        });
        if (isPpraInviteRequired) {
          form.setError('inviteCode', { type: 'validate', message: 'Invalid invite code' });
        }
      }
    }
  };

  /**
   * Handle trial subscription with $1 authorization hold verification.
   * This verifies the card has available funds before creating the trial.
   */
  const handleTrialSubmit = async (values: PaymentFormState, leadResponse: { userId: string }) => {
    if (!elements || !stripe) return;

    try {
      analytics.callIdentifyEmail(leadResponse.userId, values.email);
    } catch (e) {
      console.warn('Analytics error', e);
    }

    // Step 1: Create verification PaymentIntent ($1 authorization hold)
    const { verificationClientSecret, verificationPaymentIntentId } =
      await trialVerificationMutation.mutateAsync({
        data: {
          email: values.email,
          couponName: values.coupon || undefined,
          zipCode: values.zip || undefined,
        },
      });

    // Step 2: Confirm the $1 PaymentIntent to authorize the card
    const { error: confirmError } = await stripe.confirmPayment({
      clientSecret: verificationClientSecret,
      elements,
      confirmParams: {
        payment_method_data: {
          billing_details: { address: { country: 'US', postal_code: values.zip } },
        },
      },
      redirect: 'if_required', // Don't redirect, handle inline
    });

    if (confirmError) {
      throw new PaymentError(confirmError.message ?? 'Card verification failed. Please try a different card.');
    }

    // Step 3: Confirm trial verification (cancels the $1 hold and creates trial subscription)
    const { subscriptionId } = await confirmTrialVerificationMutation.mutateAsync({
      data: {
        verificationPaymentIntentId,
        couponName: values.coupon || undefined,
        zipCode: values.zip || undefined,
        autoRenewal: !isSinglePaymentEnabled,
      },
    });

    // Step 4: Confirm subscription to get auth token and log in the user
    const authResponse = await confirmSubscriptionMutation.mutateAsync({
      data: { subscriptionId },
    });
    authService.login(authResponse);
    analytics.callIdentifyMembership(authResponse.userId, values.email, 'member');

    // Step 5: Navigate directly to questionnaire (skip verify page since payment already confirmed)
    navigate(ROUTE_QUESTIONNAIRE_V2, { replace: true });
  };

  /**
   * Handle regular (non-trial) subscription payment flow.
   */
  const handleRegularSubmit = async (values: PaymentFormState, leadResponse: { userId: string }) => {
    if (!elements || !stripe) return;

    // Create membership/subscription
    const { type, clientSecret, subscriptionId } = await membershipMutation.mutateAsync({
      data: {
        email: values.email,
        couponName: values.coupon,
        zipCode: values.zip,
        autoRenewal: !isSinglePaymentEnabled,
        isTrial: false,
      },
    });

    // Determine whether to confirm a payment or setup intent based on type
    const confirmIntent = type === 'payment' ? stripe.confirmPayment : stripe.confirmSetup;

    try {
      analytics.callIdentifyEmail(leadResponse.userId, values.email);
    } catch (e) {
      console.warn('Analytics error', e);
    }

    // Confirm payment with Stripe and handle redirect
    const returnUrl = new URL(`${window.location.origin.toString()}${ROUTE_PAYMENT_VERIFY_PAYMENT_V2}`);
    returnUrl.searchParams.set('subscriptionId', subscriptionId);

    const { error } = await confirmIntent({
      clientSecret,
      elements,
      confirmParams: {
        return_url: returnUrl.toString(),
        payment_method_data: { billing_details: { address: { country: 'US', postal_code: values.zip } } },
      },
    });
    if (error) {
      throw new PaymentError(error.message ?? 'An error occurred processing payment');
    }

    // Important: confirmIntent above will redirect to verify payment page
  };

  // Main form submission handler that processes payment
  const handleSubmit = form.handleSubmit(async (values) => {
    if (!elements || !stripe) return;

    try {
      setPartialState({ isSubmitting: true });
      // Create lead using the mutation
      const leadResponse = await captureLead(values.email, inviteCode ?? undefined);

      // Ensure we have a userId from the lead response
      if (!leadResponse?.userId) {
        throw new Error('Failed to create lead: No userId returned');
      }

      // Submit payment form to Stripe for validation
      const { error: submitError } = await elements.submit();
      if (submitError) {
        toast({
          ...TOAST_DEFAULT_OPTIONS,
          title: 'Payment Error',
          description: submitError.message || 'Please check your payment information and try again.',
          status: 'error',
        });
        return;
      }

      try {
        // Use different flow for trial vs regular subscriptions
        if (isTrial) {
          await handleTrialSubmit(values, leadResponse);
        } else {
          await handleRegularSubmit(values, leadResponse);
        }
      } catch (error) {
        // Handling until beta
        if (
          error instanceof AxiosError &&
          error?.response?.data?.title &&
          error.response.data.title === 'Beta Test Full'
        ) {
          analytics.callIdentifyEmail(leadResponse.userId, values.email);
          analytics.callRevenue(error.response.data.detail.finalAmount / 100);
          toast({
            ...TOAST_DEFAULT_OPTIONS,
            duration: 30000,
            position: 'top',

            render: getCACToastRender(),
          });
        } else if (
          error instanceof AxiosError &&
          error?.response?.data?.title === 'User already subscribed'
        ) {
          // Handle already subscribed error
          toast({
            ...TOAST_DEFAULT_OPTIONS,
            title: 'Already Subscribed',
            description: 'You already have an active subscription. Redirecting to sign in...',
            status: 'info',
            duration: 10000
          });

          // Redirect to sign in with email pre-filled after a delay
          setTimeout(() => {
            const email = values.email;
            navigate(`${ROUTE_SIGN_IN_ENTER_EMAIL}?email=${encodeURIComponent(email)}`);
          }, 2000);
        } else {
          // Handle general payment errors with appropriate messages
          const defaultErrorMessage = 'We encountered an issue processing your payment. Please try again later.';
          const errorMessage =
            error instanceof AxiosError
              ? error?.response?.data?.message ?? error?.response?.data?.detail ?? defaultErrorMessage
              : error instanceof Error
              ? error?.message ?? defaultErrorMessage
              : defaultErrorMessage;

          toast({
            ...TOAST_DEFAULT_OPTIONS,
            title: 'Payment Error',
            description: errorMessage,
            status: 'error',
          });
        }
      }
    } catch (error) {
      // Handle general payment errors with appropriate messages
      const defaultErrorMessage = 'We encountered an issue processing your payment. Please try again later.';

      toast({
        ...TOAST_DEFAULT_OPTIONS,
        title: 'Payment Error',
        description: defaultErrorMessage,
        status: 'error',
      });
    } finally {
      setPartialState({ isSubmitting: false });
    }
  });

  // Display error from URL parameters if present
  useEffect(() => {
    if (errorToShow) {
      toast({
        ...TOAST_DEFAULT_OPTIONS,
        title: 'Error',
        description: errorToShow,
        status: 'error',
      });
    }
  }, [errorToShow]); // eslint-disable-line react-hooks/exhaustive-deps

  useOnMount(() => {
    async function initialize() {
      // Initialize session
      const response = await initializeSession();
      if (response) {
        if (response.email) {
          setPartialState({ isEmailEditable: false });
          form.setValue('email', response.email);
        }
      }

      // Fetch price data
      getPrice();
    }
    initialize();
  });

  // Get the current price from the priceData if available
  const currentPrice = priceData?.lines?.[0]?.amount ? priceData.lines[0].amount : null;

  /**
   * Handle Express Checkout payment confirmation
   * This is called when a user clicks on Apple Pay, Google Pay, etc.
   * @param event - The Express Checkout confirmation event containing billing details
   */
  const handleExpressCheckout = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    if (!stripe || !elements) {
      event.paymentFailed({ reason: 'fail' });
      return;
    }

    // Declare checkoutEmail outside try block so it's accessible in catch block
    let checkoutEmail: string | undefined;

    try {
      setPartialState({ isSubmitting: true });

      // Use only session email from form
      checkoutEmail = form.getValues('email');

      const zip = event.billingDetails?.address?.postal_code ||
                  (event as any).shippingAddress?.postalCode ||
                  form.getValues('zip') || '';

      // Validate session email exists
      if (!checkoutEmail) {
        // Express Checkout requires session email - this is a user error
        // Don't log to console to avoid sending to Datadog
        toast({
          ...TOAST_DEFAULT_OPTIONS,
          title: 'Email Required',
          description: 'You must provide an email address.',
          status: 'error',
        });

        event.paymentFailed({ reason: 'fail' });
        return;
      }

      // Create lead
      const leadResponse = await captureLead(checkoutEmail, inviteCode ?? undefined);

      if (!leadResponse?.userId) {
        throw new Error('Failed to create lead: No userId returned');
      }

      // Submit the elements to get the payment method
      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new PaymentError(submitError.message || 'Failed to submit payment details');
      }

      try {
        analytics.callIdentifyEmail(leadResponse.userId, checkoutEmail);
      } catch (e) {
        console.warn('Analytics error', e);
      }

      // Use different flow for trial vs regular subscriptions
      if (isTrial) {
        // Trial flow: Use $1 authorization hold verification
        // Step 1: Create verification PaymentIntent ($1 authorization hold)
        const { verificationClientSecret, verificationPaymentIntentId } =
          await trialVerificationMutation.mutateAsync({
            data: {
              email: checkoutEmail,
              couponName: form.getValues('coupon') || tempCoupon || undefined,
              zipCode: zip || undefined,
            },
          });

        // Step 2: Confirm the $1 PaymentIntent to authorize the card
        const { error: confirmError } = await stripe.confirmPayment({
          clientSecret: verificationClientSecret,
          elements,
          confirmParams: {
            payment_method_data: {
              billing_details: { address: { country: 'US', postal_code: zip } },
            },
          },
          redirect: 'if_required', // Don't redirect, handle inline
        });

        if (confirmError) {
          throw new PaymentError(confirmError.message ?? 'Card verification failed. Please try a different card.');
        }

        // Step 3: Confirm trial verification (cancels the $1 hold and creates trial subscription)
        const { subscriptionId } = await confirmTrialVerificationMutation.mutateAsync({
          data: {
            verificationPaymentIntentId,
            couponName: form.getValues('coupon') || tempCoupon || undefined,
            zipCode: zip || undefined,
            autoRenewal: !isSinglePaymentEnabled,
          },
        });

        // Step 4: Confirm subscription to get auth token and log in the user
        const authResponse = await confirmSubscriptionMutation.mutateAsync({
          data: { subscriptionId },
        });
        authService.login(authResponse);
        analytics.callIdentifyMembership(authResponse.userId, checkoutEmail, 'member');

        // Step 5: Navigate directly to questionnaire (skip verify page since payment already confirmed)
        navigate(ROUTE_QUESTIONNAIRE_V2, { replace: true });
      } else {
        // Regular flow: Create subscription and confirm payment
        const { type, clientSecret, subscriptionId } = await membershipMutation.mutateAsync({
          data: {
            email: checkoutEmail,
            couponName: form.getValues('coupon') || tempCoupon,
            zipCode: zip,
            autoRenewal: !isSinglePaymentEnabled,
            isTrial: false,
          },
        });

        // Determine the confirmation method based on type
        const confirmMethod = type === 'payment' ? stripe.confirmPayment : stripe.confirmSetup;

        // Confirm the payment/setup with redirect
        const { error: confirmError } = await confirmMethod({
          clientSecret,
          elements,
          confirmParams: {
            return_url: `${window.location.origin.toString()}${ROUTE_PAYMENT_VERIFY_PAYMENT_V2}?subscriptionId=${subscriptionId}`,
          },
          redirect: 'always',
        });

        if (confirmError) {
          throw new PaymentError(confirmError.message || 'Payment confirmation failed');
        }
      }
    } catch (error) {
      // Express Checkout failed - handle the error without logging to console
      // to avoid sending user payment errors to Datadog

      event.paymentFailed({ reason: 'fail' });

      let errorMessage = 'Payment failed. Please try again.';
      let errorTitle = 'Payment Error';

      // Check if it's an Axios error with response data
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as AxiosError<{ title?: string; detail?: string }>;

        if (axiosError.response?.data?.title === 'User already subscribed') {
          errorTitle = 'Already Subscribed';
          errorMessage = 'You already have an active subscription. Redirecting to sign in...';

          // Redirect to sign in with email pre-filled after a delay
          // Use session email from form
          const redirectEmail = form.getValues('email') || '';
          setTimeout(() => {
            navigate(`${ROUTE_SIGN_IN_ENTER_EMAIL}?email=${encodeURIComponent(redirectEmail)}`);
          }, 2000);
        } else if (axiosError.response?.data?.detail) {
          errorMessage = axiosError.response.data.detail;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      toast({
        ...TOAST_DEFAULT_OPTIONS,
        title: errorTitle,
        description: errorMessage,
        status: 'error',
      });
    } finally {
      setPartialState({ isSubmitting: false });
    }
  };

  // Export public API of the hook
  return {
    email,
    form,
    handleSubmit,
    handleExpressCheckout,
    couponValidationError: validationError,
    getPrice,
    joinWaitlist,
    coupon: tempCoupon,
    setCoupon,
    priceData,
    isCouponVisible,
    showCoupon,
    isSubmitting: isSubmitting || isPending,
    isEmailEditable,
    setIsEmailEditable,
    inviteCode,
    currentPrice,
    isPpraInviteRequired,
    clearedWaitList,
    priceRecurring: getPriceData?.recurring,
    isTrial,
    isPriceLoading: isPending,
  };
}
